import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Trash2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useAuth } from '../../contexts/AuthContext';
import { deleteComment } from '../../lib/services/feedService';
import toast from 'react-hot-toast';
import type { CommentWithAuthor } from '../../types/database';

interface CommentItemProps {
  comment: CommentWithAuthor;
  onCommentDeleted?: (commentId: string) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({ 
  comment,
  onCommentDeleted
}) => {
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const isAuthor = user && user.id === comment.user_id;

  // Obter username e avatar do autor a partir do profile
  const authorUsername = comment.author_profile?.username ?? 'Usuário';
  const authorAvatarUrl = comment.author_profile?.avatar_url ?? null;

  // Sanitizar conteúdo do comentário
  const sanitizedContent = useMemo(() => {
    return comment.content ? DOMPurify.sanitize(comment.content, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] }) : '';
  }, [comment.content]);

  // Formatar a data de criação com verificação de valor válido
  const formattedDate = React.useMemo(() => {
    try {
      if (!comment.created_at) return 'Data desconhecida';
      
      const date = new Date(comment.created_at);
      if (isNaN(date.getTime())) return 'Data inválida';
      
      return formatDistanceToNow(date, {
        addSuffix: true,
        locale: ptBR
      });
    } catch (error) {
      console.error('Erro ao formatar data do comentário:', error);
      return 'Data indisponível';
    }
  }, [comment.created_at]);

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir este comentário?')) {
      return;
    }

    setIsDeleting(true);
    try {
      const success = await deleteComment(comment.id);
      if (success) {
        toast.success('Comentário excluído com sucesso!');
        if (onCommentDeleted) {
          onCommentDeleted(comment.id);
        }
      } else {
        toast.error('Não foi possível excluir o comentário.');
      }
    } catch (error) {
      console.error('Erro ao excluir comentário:', error);
      toast.error('Erro ao excluir o comentário.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="py-3 flex items-start" data-comment-id={comment.id}>
      <Link to={`/profile/${comment.author_profile?.username}`} className="flex-shrink-0">
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm overflow-hidden bg-indigo-100 text-indigo-600 dark:bg-indigo-500 dark:text-indigo-100">
          {authorAvatarUrl ? (
            <img
              src={authorAvatarUrl}
              alt={authorUsername}
              className="w-full h-full object-cover"
            />
          ) : (
            authorUsername.charAt(0).toUpperCase()
          )}
        </div>
      </Link>
      
      <div className="ml-3 flex-1">
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between mb-1">
            <Link 
              to={`/profile/${comment.author_profile?.username}`}
              className="font-medium text-gray-900 hover:text-indigo-600 dark:text-gray-100 dark:hover:text-indigo-400 transition-colors"
            >
              {authorUsername}
            </Link>
            
            {isAuthor && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-gray-600 transition-colors"
                aria-label="Excluir comentário"
              >
                {isDeleting ? (
                  <div className="w-4 h-4 border-2 border-t-2 border-gray-500 dark:border-gray-400 rounded-full animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            )}
          </div>
          
          {sanitizedContent && (
            <div 
              className="whitespace-pre-line prose prose-sm max-w-none text-gray-800 dark:text-gray-200 dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          )}
          
          <div className="mt-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">{formattedDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentItem; 